import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Country } from '~/Api/Country';
import { ReferenceBasketVersion } from '~/Api/RoPriceReferencing/MaxPrice';
import { ModuleType, UserProductGroup } from '~/Api/User/ProductGroup';
import { ErrorResponse, Product, RoMaxPrice as ApiRoMaxPrice, Update } from '~/Api/User/ProductGroup/RoMaxPrice';
import { AppError, handleError } from '~/ErrorHandler';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import {
	fetchReferenceBasketVersions,
	Module,
	pruneReferenceBasketVersions,
	selectReferenceBasketVersions,
} from '~/PriceReferencing';
import { PricedProduct, Product as GeneralProduct } from '~/Product';
import { pruneDatasets } from '~/ProductDataset';
import { pruneAllPrices } from '~/ProductPrice';
import { State, ThunkDispatch } from '~/Store';

import { Action } from './Action';
import {
	commitRoMaxPriceUpdate,
	fetchReferencedProductTypes,
	fetchRoMaxPrice,
	handleCommitError,
	handleRoMaxPriceUpdateResponse,
	pruneRoMaxPrice,
	resetRoMaxPriceGroup,
	scheduleRoMaxPriceUpdate,
} from './actions';
import { selectExport, selectRoMaxPrice } from './reducer';
import RoMaxPriceContent from './RoMaxPrice';

interface BasketProduct {
	country: Country;
	product: Product;
	usedForCalculation: boolean;
}
interface BasketOptionalProduct {
	country: Country;
	product: Product | null;
	usedForCalculation: boolean;
}

const containsCountry = (countries: Country[], code: string): boolean =>
	countries.find((country) => country.alpha2 === code) !== undefined;

const compareReferences = (
	reference: BasketProduct | BasketOptionalProduct,
	otherReference: BasketProduct | BasketOptionalProduct
): number => {
	if (reference.product !== null && otherReference.product === null) {
		return -1;
	}
	if (reference.product === null && otherReference.product !== null) {
		return 1;
	}
	const comparison = reference.country.alpha2.localeCompare(otherReference.country.alpha2);
	if (comparison !== 0) {
		return comparison;
	}
	if (reference.product !== null && otherReference.product !== null) {
		return reference.product.product.brandName.localeCompare(otherReference.product.product.brandName);
	}
	return 0;
};

const groupProducts = memoizeOne(
	(roMaxPrice: ApiRoMaxPrice | null, versions: ReferenceBasketVersion[] | null, showAll: boolean) => {
		const version =
			versions !== null && roMaxPrice !== null
				? versions.find((version) => version.id === roMaxPrice.reference.referenceBasketVersion) ?? null
				: null;
		const referenceCountries = version !== null ? version.referenceCountries : [];
		const products = roMaxPrice !== null && version !== null ? roMaxPrice.products : [];
		const nonsetCountries = new Set(referenceCountries?.map((country) => country.alpha2));
		const usedForCalculationIds = new Set([
			...(roMaxPrice?.finalCalculation?.canamedPrice?.fromProducts?.map((product) => product.id) ?? []),
			...(roMaxPrice?.finalCalculation?.publicCatalogPrice?.fromProducts?.map((product) => product.id) ?? []),
		]);

		const romanianProducts: BasketOptionalProduct[] = [];
		const referencedProducts: BasketOptionalProduct[] = [];
		const otherProducts: BasketProduct[] = [];
		let alternativeProductsCount = 0;

		products.forEach((product) => {
			const { country } = product.product.dataset.source;
			alternativeProductsCount += product.selected ? 0 : 1;

			if (!showAll && !product.selected) {
				return;
			}

			if (country.alpha2 === 'RO') {
				romanianProducts.push({
					country,
					product,
					usedForCalculation: usedForCalculationIds.has(product.product.id),
				});
				return;
			}
			if (containsCountry(referenceCountries ?? [], country.alpha2)) {
				referencedProducts.push({
					country,
					product,
					usedForCalculation: usedForCalculationIds.has(product.product.id),
				});
				nonsetCountries.delete(country.alpha2);
				return;
			}
			if (showAll) {
				otherProducts.push({
					country,
					product,
					usedForCalculation: usedForCalculationIds.has(product.product.id),
				});
			}
		});

		if (romanianProducts.length === 0) {
			romanianProducts.push({
				country: new Country('RO', 'Romania'),
				product: null,
				usedForCalculation: false,
			});
		}

		referenceCountries?.forEach((country) => {
			if (nonsetCountries.has(country.alpha2)) {
				referencedProducts.push({
					country,
					product: null,
					usedForCalculation: false,
				});
			}
		});

		romanianProducts.sort(compareReferences);
		referencedProducts.sort(compareReferences);
		otherProducts.sort(compareReferences);

		const basketProducts = [...romanianProducts, ...referencedProducts, ...otherProducts].filter(
			(product) => product.product !== null
		) as BasketProduct[];
		const pricedProducts = basketProducts.map(
			(product) => new PricedProduct(product.product, product.product.product, null)
		);

		return {
			romanianProducts,
			otherProducts,
			pricedProducts,
			alternativeProductsCount,
			referenceBasketVersion: version,
			referencedProducts,
		};
	}
);

const mapStateToProps = (state: State, { group }: { group: UserProductGroup }) => {
	const roMaxPriceState = selectRoMaxPrice(state.roMaxPrice, group.id);
	const roMaxPrice = roMaxPriceState?.roMaxPrice ?? null;
	const versions: ReferenceBasketVersion[] | null = selectReferenceBasketVersions(
		state.priceReferencing,
		Module.roMaxPrice
	);

	return {
		...groupProducts(roMaxPrice, versions, state.products.showAllProducts),
		roMaxPrice,
		isFetching: Boolean(roMaxPriceState?.isFetching),
		validationResult: selectExport(state.roMaxPrice, group.id)?.validationResult ?? null,
		versions,
		isAdditionalPriceRoofAllowed:
			state.accessToken.accessToken?.subscription?.systemModules?.has(ModuleType.roMaxPriceAdditionalPriceRoof) ??
			false,
	};
};

const actions = {
	onFetch: (group: UserProductGroup) => async (dispatch: ThunkDispatch<AnyAction>) => {
		dispatch(fetchReferenceBasketVersions(Module.roMaxPrice));
		dispatch(fetchReferencedProductTypes());

		try {
			await dispatch(commitRoMaxPriceUpdate(group.id));
		} catch (error) {
			dispatch(handleCommitError(error));
		}

		try {
			await dispatch(fetchRoMaxPrice(group.id));
		} catch (error) {
			if (!(error instanceof ErrorResponse)) {
				dispatch(handleError(error));
				return;
			}
			error.handleProductGroupNotFound();
			dispatch(handleError(error));
		}
	},
	onPrune: (group: UserProductGroup) => async (dispatch: ThunkDispatch<AnyAction>) => {
		try {
			await dispatch(commitRoMaxPriceUpdate(group.id));
		} catch (error) {
			dispatch(handleCommitError(error));
		}
		dispatch(pruneRoMaxPrice(group.id));
		dispatch(pruneReferenceBasketVersions());
		dispatch(pruneAllPrices());
		dispatch(pruneDatasets());
	},
	onSave: (group: UserProductGroup) => async (dispatch: ThunkDispatch<AnyAction>) => {
		try {
			await dispatch(commitRoMaxPriceUpdate(group.id));
		} catch (error) {
			dispatch(handleRoMaxPriceUpdateResponse(error, group.id));
		}
	},
	onUpdate: (group: UserProductGroup, update: Update) => async (dispatch: ThunkDispatch<AnyAction>) => {
		try {
			await dispatch(scheduleRoMaxPriceUpdate(group.id, update));
		} catch (error) {
			dispatch(handleRoMaxPriceUpdateResponse(error, group.id));
		}
	},
	onResetProductList: (groupId: string) => async (dispatch: ThunkDispatch<AnyAction>) => {
		try {
			await dispatch(resetRoMaxPriceGroup(groupId));
		} catch (error) {
			dispatch(handleError(error));
		}
	},
	onDeleteItems:
		(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void) =>
		async (dispatch: ThunkDispatch<AnyAction>) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error: unknown) {
				dispatch(handleError(error as AppError));
			}
		},
};

export default connect(mapStateToProps, actions)(RoMaxPriceContent);
